<script>
/*
This whole file has a tricky workaround to handle UTC and GMT differences.
The main reason is that the calendar element ui is based in UTC, but when you get a value from it, it turns it into GMT.

For example: If you select a random date such as 15/07, the calendar element guess it's `2022-07-15T00:00:00.000Z` but when
you save the value in a prop it saves as `2022-07-14T21:00:00.000 GMT-0300`.

It's a reported bug, you can find the demos in their webpage bugging https://element.eleme.io/#/es/component/calendar
(Click on a date in Basic Calendar demo and see what happens)

I've marked the 'tricky' parts with a comment to identify faster those parts. Find the word 'workaround'.
*/
import moment from "moment";
import {
  format,
  addMilliseconds,
  startOfDay,
  isBefore,
  parseISO,
} from "date-fns";
import Spinner from "@/components/ui/Spinner";
import { mapGetters } from "vuex";
import userService from "@/services/user";
import helperService from "@/services/helper";
import ModalAppointment from "@/components/appointment/ModalAppointment";
import SlotsCalendar from "@/components/appointment/SlotsCalendar";
import institutionService from "@/services/institution";
import slotService from "@/services/slot";
import doctorService from "@/services/doctor";
import appointmentService from "@/services/appointment";
import pagination from "@/mixins/pagination";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";

import {
  SLOT_ATTENTION_TYPES,
  APPOINTMENTS_STATUS,
  VIDEOROOM_MM_OFFSET_PRE,
  VIDEOROOM_MM_OFFSET_POST,
  getPatientAdmissionStatus,
} from "@/services/constants";
import {
  isAppointmentBookingEnabled,
  isAgendaVirtualEnabled,
  isManualManagementEnabled,
  isAppointmentStatusEnabled,
  isAdmissionForDeragopyanEnabled,
  isAdmissionTimestampEnabled,
  isAssistantHistoricalAgendaEnabled,
  isObservationEditEnabled,
  isSlotDisableHiddenForDoctor,
  isHL7AgendaEnabled,
} from "@/services/config";

export default {
  name: "AppointmentsView",

  mixins: [pagination],

  components: {
    Spinner,
    ModalAppointment,
    SlotsCalendar,
    BaseSubNav,
  },

  data() {
    return {
      doctors: [],
      institutions: [],
      medicalSpecialties: [],
      selectedDate: "",
      selectedDateSlots: [],
      selectedDoctorId: "",
      selectedInstitutionId: "",
      selectedMedicalSpecialtyId: "",
      selectedStatus: "",
      selectedSlot: {},
      isModalVisible: false,
      isLoadingSlots: false,
      isLoadingInstitutions: false,
      isLoadingDoctors: false,
      isLoadingDateSlots: false,
      appointmentsStatus: APPOINTMENTS_STATUS,
      isAppointmentBookingEnabled,
      isManualManagementEnabled,
      isAppointmentStatusEnabled,
      isAdmissionForDeragopyanEnabled,
      isAdmissionTimestampEnabled,
      isObservationEditEnabled,
      isSlotDisableHiddenForDoctor,
      isHL7AgendaEnabled,
      observationsBackup: {},
      editingObservations: {},
      savingObservations: {},
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isAssistant", "isDoctor", "isPatient", "user"]),

    isHL7AgendaMode() {
      return isHL7AgendaEnabled && (this.isAdmin || this.isAssistant);
    },

    canLoadSlots() {
      if (this.isHL7AgendaMode) {
        return (
          !!this.selectedDate &&
          !!this.selectedDoctorId &&
          !!this.selectedInstitutionId
        );
      }
      return (
        !!this.selectedDate &&
        !!this.selectedDoctorId &&
        !!this.selectedInstitutionId &&
        !!this.selectedMedicalSpecialtyId
      );
    },

    canNavigatePastDates() {
      return (
        isAssistantHistoricalAgendaEnabled &&
        (this.isAssistant || this.isDoctor || this.isAdmin)
      );
    },

    isSelectedDateInPast() {
      if (!this.selectedDate) return false;
      return isBefore(parseISO(this.selectedDate), startOfDay(new Date()));
    },

    isAgendaReadOnlyMode() {
      return this.canNavigatePastDates && this.isSelectedDateInPast;
    },

    filteredSlots() {
      const status = this.selectedStatus;
      if (status === "free")
        return this.selectedDateSlots.filter((slot) => slot.available);
      if (status === "busy")
        return this.selectedDateSlots.filter((slot) => !slot.available);
      const appointmentStatuses = [
        "pending",
        "admitted",
        "started",
        "done",
        "cancelled",
      ];
      if (appointmentStatuses.includes(status))
        return this.selectedDateSlots.filter(
          (slot) => slot.appointment?.status === status
        );
      return this.selectedDateSlots;
    },

    getSelectedMedicalSpecialty() {
      return this.medicalSpecialties?.find(
        (medicalSpecialty) =>
          medicalSpecialty._id === this.selectedMedicalSpecialtyId
      );
    },
  },

  watch: {
    selectedInstitutionId() {
      this.selectedDateSlots = [];
      if (this.selectedInstitutionId) {
        this.getDoctorsByInstitution(this.selectedInstitutionId);
      } else {
        this.doctors = [];
        this.medicalSpecialties = [];
        this.selectedMedicalSpecialtyId = "";

        if (!this.isDoctor) {
          this.selectedDoctorId = "";
        }
      }
    },

    selectedDoctorId() {
      this.selectedDateSlots = [];
      if (this.selectedDoctorId) {
        this.getMedicalSpecialtiesByDoctor(this.selectedDoctorId);
        if (this.isHL7AgendaMode && this.selectedDate) {
          this.getSlotsByDate();
        }
      } else {
        this.medicalSpecialties = [];
        this.selectedMedicalSpecialtyId = "";
      }
    },

    selectedMedicalSpecialtyId() {
      this.selectedDateSlots = [];
      this.getSlotsByDate();
    },

    selectedDate() {
      if (this.selectedDate) {
        this.getSlotsByDate();
      }
    },
  },

  created() {
    console.log("isManualManagementEnabled:", isManualManagementEnabled);
    this.$bus.$on("selected-date", (selectedDate) => {
      const isPast = isBefore(parseISO(selectedDate), startOfDay(new Date()));
      if (!isPast || this.canNavigatePastDates) {
        this.selectedDate = selectedDate;
      } else {
        this.selectedDateSlots = [];
        this.selectedDate = "";
      }
    });

    this.$bus.$on("appointment-created", (slot, appointment) => {
      const index = this.selectedDateSlots.findIndex((elem) => {
        return elem._id === slot._id;
      });

      const createdAppointment = {
        ...this.selectedDateSlots[index],
        available: false,
        appointment,
      };

      this.$set(this.selectedDateSlots, index, createdAppointment);
    });

    this.$bus.$on("appointment-cancelled", (cancelledAppointment) => {
      const index = this.selectedDateSlots.findIndex(
        (elem) => elem.appointment?._id === cancelledAppointment._id
      );

      const updatedSlot = {
        ...this.selectedDateSlots[index],
        available: true,
        appointment: undefined,
      };

      this.$set(this.selectedDateSlots, index, updatedSlot);
    });

    if (this.isDoctor) {
      this.getDoctorDetail();
    } else {
      this.getInstitutions();
    }
  },

  methods: {
    startInstantAppointment() {
      const newSlot = {
        attentionType: ["in-person"], //TODO CHECK THIS
        available: true,
        startsAt: null,
        endsAt: null,
        isVisibleByPatient: true,
        medicalSpecialties: this.medicalSpecialties.filter(
          (elem) => elem._id === this.selectedMedicalSpecialtyId
        ),
        doctor: this.doctors.find((elem) => elem._id === this.selectedDoctorId),
        institution: this.institutions.find(
          (elem) => elem._id === this.selectedInstitutionId
        ),
      };

      this.selectedSlot = newSlot;
      this.isModalVisible = true;
    },

    async startAppointment(appointmentId) {
      if (!this.isAdmissionForDeragopyanEnabled) return;
      const slot = this.selectedDateSlots.find(
        (s) => s.appointment?._id === appointmentId
      );
      const wasAdmitted = !!slot?.appointment?.admittedAt;
      const updatedAppointment = await appointmentService.startAppointment(
        appointmentId
      );
      this.updateSlotAppointmentStatus(
        appointmentId,
        updatedAppointment.status
      );
      // Solo cuando el turno pasa de no-admitido a admitido (admitOnStart, CNC).
      if (!wasAdmitted && updatedAppointment.admittedAt) {
        this.$message.success("Turno admitido con éxito");
      }
    },

    updateSlotAppointmentStatus(appointmentId, newStatus) {
      const index = this.selectedDateSlots.findIndex(
        (slot) => slot.appointment?._id === appointmentId
      );
      if (index !== -1 && this.selectedDateSlots[index].appointment) {
        const updatedSlot = {
          ...this.selectedDateSlots[index],
          appointment: {
            ...this.selectedDateSlots[index].appointment,
            status: newStatus,
          },
        };
        this.$set(this.selectedDateSlots, index, updatedSlot);
      }
    },

    async getDoctorDetail(doctorId) {
      if (this.isDoctor) {
        doctorId = this.$store.state.user._id;
      }

      const doctorDetails = await doctorService.getDoctorDetails(doctorId);
      const { doctor } = doctorDetails;

      this.doctors = [doctorDetails];
      this.selectedDoctorId = doctorDetails._id;
      this.institutions = doctor.institutions;
      this.medicalSpecialties = doctor.medicalSpecialties;
    },

    async getInstitutions() {
      this.isLoadingInstitutions = true;
      try {
        const institutions = await institutionService.getInstitutions();
        this.institutions = institutions;
      } finally {
        this.isLoadingInstitutions = false;
      }
    },

    async getDoctorsByInstitution(institutionId) {
      this.isLoadingDoctors = true;
      try {
        const doctors = await userService.getDoctorsByInstitution(
          institutionId
        );
        this.doctors = doctors;
      } finally {
        this.isLoadingDoctors = false;
      }
    },

    getMedicalSpecialtiesByDoctor(doctorId) {
      if (this.doctors) {
        const [doctor] = this.doctors.filter(
          (doctor) => doctor._id === doctorId
        );
        const medicalSpecialties = doctor.doctor.medicalSpecialties;
        this.medicalSpecialties = medicalSpecialties;
      }
    },

    async getSlotsByDate() {
      this.selectedDateSlots = [];

      if (!this.canLoadSlots) return;

      this.isLoadingSlots = true;
      try {
        if (this.isHL7AgendaMode) {
          const res = await slotService.getSlotsByDoctor({
            doctorId: this.selectedDoctorId,
            params: {
              startsAt: { $gte: this.getDateFirstHourOfDay(this.selectedDate) },
              endsAt: { $lte: this.getDateLastHourOfDay(this.selectedDate) },
              populate: [
                {
                  path: "appointment",
                  populate: [{ path: "patient" }, { path: "medicalSpecialty" }],
                },
                { path: "medicalSpecialties institution doctor" },
              ],
              offset: this.pagination.offset,
              limit: this.pagination.limit,
            },
          });
          res.docs.forEach((slot) => {
            if (slot.appointment) {
              slot.appointment.observation = slot.appointment.observation || "";
            }
          });
          this.selectedDateSlots = res.docs;
          this.observationsBackup = res.docs.reduce((acc, slot) => {
            if (slot.appointment?._id) {
              acc[slot.appointment._id] = slot.appointment.observation;
            }
            return acc;
          }, {});
          this.editingObservations = {};
          this.savingObservations = {};
          this.pagination.total = res.total;
          return;
        }

        const query = {
          startsAt: { $gte: this.getDateFirstHourOfDay(this.selectedDate) },
          endsAt: { $lte: this.getDateLastHourOfDay(this.selectedDate) },
          doctor: this.selectedDoctorId,
          medicalSpecialties: this.selectedMedicalSpecialtyId,
          institution: this.selectedInstitutionId,
          populate: [
            {
              path: "appointment",
              populate: [
                { path: "videoRoom" },
                { path: "patient" },
                { path: "medicalSpecialty" },
              ],
            },
            { path: "doctor" },
            { path: "medicalSpecialties" },
          ],
        };

        const res = await slotService.getSlotsByDate({
          ...query,
          offset: this.pagination.offset,
          limit: this.pagination.limit,
        });

        res.docs.forEach((slot) => {
          if (slot.appointment) {
            slot.appointment.observation = slot.appointment.observation || "";
          }
        });
        this.selectedDateSlots = res.docs;
        this.observationsBackup = res.docs.reduce((acc, slot) => {
          if (slot.appointment?._id) {
            acc[slot.appointment._id] = slot.appointment.observation;
          }
          return acc;
        }, {});
        this.editingObservations = {};
        this.savingObservations = {};
        this.pagination.total = res.total;
      } finally {
        this.isLoadingSlots = false;
      }
    },

    getAttentionTypeName(type) {
      const attentionType = SLOT_ATTENTION_TYPES.find(
        (elem) => elem.value === type
      );
      return attentionType.name;
    },

    hasActiveRoom(slot) {
      if (slot.appointment?.videoRoom?.status === "in-progress") {
        return true;
      } else {
        return false;
      }
    },

    isVideoRoomEnabled(slot) {
      if (slot.appointment) {
        const now = new Date();

        const offsetStartsAt = new Date(slot.appointment.startsAt);
        const offsetEndsAt = new Date(slot.appointment.endsAt);

        offsetStartsAt.setMinutes(
          offsetStartsAt.getMinutes() - VIDEOROOM_MM_OFFSET_PRE
        );
        offsetEndsAt.setMinutes(
          offsetEndsAt.getMinutes() + VIDEOROOM_MM_OFFSET_POST
        );

        return (
          slot.appointment.attentionType === "virtual" &&
          offsetStartsAt <= now &&
          offsetEndsAt >= now
        );
      } else {
        return false;
      }
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    getDateFirstHourOfDay(date) {
      // workaround
      const offset = moment().utcOffset() * -1;
      const firstHour = moment(date)
        .add(offset, "minutes")
        .startOf("day")
        .utc()
        .format();

      return firstHour;
      // workaround
    },

    getDateLastHourOfDay(date) {
      // workaround
      const offset = moment().utcOffset() * -1;
      const lastHour = moment(date)
        .add(offset, "minutes")
        .endOf("day")
        .utc()
        .format();

      return lastHour;
      // workaround
    },

    getStartAndEndHours(startsAt, endsAt) {
      const parsedStartHour = format(new Date(startsAt), "HH:mm");

      let parsedEndHour = format(addMilliseconds(new Date(endsAt), 1), "HH:mm");

      // workaround
      if (parsedEndHour === "00:00") {
        parsedEndHour = "24:00";
      }
      // workaround

      return `${parsedStartHour} - ${parsedEndHour}`;
    },

    goToAppointment(appointmentId) {
      this.$router.push({
        name: "appointment",
        params: { appointmentId },
      });
    },

    getAppointmentLabel(slot) {
      if (slot.available) {
        return "Disponible";
      }
      if (slot.appointment) {
        if (this.isPatient && slot.appointment.patient?._id !== this.user._id) {
          return "Ocupado";
        }
        return this.getFullName(slot.appointment.patient);
      }
      return "No disponible";
    },

    showModal(slot) {
      if (this.isAgendaReadOnlyMode) return;
      if (!isManualManagementEnabled && slot.available) return;
      if (slot.available || slot.appointment) {
        this.isModalVisible = true;
        this.selectedSlot = slot;
      }
    },

    closeModal() {
      this.isModalVisible = false;
    },

    canEditSlotObservation(slot) {
      return (
        this.isObservationEditEnabled &&
        !!slot?.appointment &&
        (this.isAdmin || this.isAssistant)
      );
    },

    shouldShowAddObservationButton(slot) {
      const id = slot?.appointment?._id;
      if (!id) return false;
      return (
        !(this.observationsBackup[id] || "") && !this.editingObservations[id]
      );
    },

    isObservationDirty(slot) {
      const id = slot?.appointment?._id;
      if (!id) return false;
      const current = (slot.appointment.observation || "").trim();
      const saved = (this.observationsBackup[id] || "").trim();
      return current !== saved;
    },

    startEditingObservation(slot) {
      const id = slot.appointment._id;
      this.$set(this.editingObservations, id, true);
      this.$nextTick(() => {
        const ref = this.$refs[`obs-${id}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) el.focus();
      });
    },

    isObservationSaving(slot) {
      const id = slot?.appointment?._id;
      return !!id && !!this.savingObservations[id];
    },

    async saveObservation(slot) {
      if (!this.canEditSlotObservation(slot)) return;
      if (!this.isObservationDirty(slot)) return;
      const appointmentId = slot.appointment._id;
      if (this.savingObservations[appointmentId]) return;
      const trimmed = (slot.appointment.observation || "").trim();
      this.$set(this.savingObservations, appointmentId, true);
      try {
        await appointmentService.updateObservation(appointmentId, trimmed);
        this.$set(this.observationsBackup, appointmentId, trimmed);
        if (!trimmed) {
          this.$set(this.editingObservations, appointmentId, false);
        }
        this.$message.success("Observación guardada");
      } catch (err) {
        const previous = this.observationsBackup[appointmentId] || "";
        this.$set(slot.appointment, "observation", previous);
        this.$message.error(
          err.response?.data?.error || "No se pudo guardar la observación"
        );
      } finally {
        this.$set(this.savingObservations, appointmentId, false);
      }
    },

    async deleteObservation(slot) {
      if (!this.canEditSlotObservation(slot)) return;
      const appointmentId = slot.appointment._id;
      if (this.savingObservations[appointmentId]) return;
      const previous = this.observationsBackup[appointmentId] || "";
      this.$set(slot.appointment, "observation", "");
      if (!previous) {
        this.$set(this.editingObservations, appointmentId, false);
        return;
      }
      this.$set(this.savingObservations, appointmentId, true);
      try {
        await appointmentService.updateObservation(appointmentId, "");
        this.$set(this.observationsBackup, appointmentId, "");
        this.$set(this.editingObservations, appointmentId, false);
        this.$message.success("Observación borrada");
      } catch (err) {
        this.$set(slot.appointment, "observation", previous);
        this.$message.error(
          err.response?.data?.error || "No se pudo borrar la observación"
        );
      } finally {
        this.$set(this.savingObservations, appointmentId, false);
      }
    },

    getMedicalSpecialtiesNames(slot) {
      return slot.appointment
        ? slot.appointment.medicalSpecialty.name
        : slot.medicalSpecialties?.map((m) => m?.name).join(", ") || "N/A";
    },

    attentionType(slot) {
      const types = slot.appointment
        ? [slot.appointment.attentionType]
        : slot.attentionType;
      if (!isAgendaVirtualEnabled) {
        return types.filter((type) => type !== "virtual");
      }
      return types;
    },

    onPageChange() {
      this.getSlotsByDate();
    },

    cancelAppointment(appointmentId) {
      this.$confirm(`¿Desea cancelar la cita?`, "Cancelar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      }).then(() => {
        appointmentService
          .cancelAppointment(appointmentId)
          .then((cancelledAppointment) => {
            const index = this.selectedDateSlots.findIndex(
              (elem) => elem.appointment._id === cancelledAppointment._id
            );

            const updatedSlot = {
              ...this.selectedDateSlots[index],
              available: true,
              appointment: undefined,
            };

            this.$set(this.selectedDateSlots, index, updatedSlot);
          })
          .then(() => this.$message.success("Turno cancelado con éxito"));
      });
    },

    disableSlot(slotId) {
      this.$confirm(`¿Desea deshabilitar el horario?`, "Deshabilitar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      }).then(() => {
        slotService
          .disableSlotById({
            slotId,
            params: { relocate: false, remove: false },
          })
          .then(() => {
            const index = this.selectedDateSlots.findIndex(
              (elem) => elem._id === slotId
            );

            const updatedSlot = {
              ...this.selectedDateSlots[index],
              available: false,
              appointment: undefined,
            };

            this.$set(this.selectedDateSlots, index, updatedSlot);
          })
          .then(() =>
            this.$message.success("Se deshabilitó el horario con éxito")
          );
      });
    },

    getStatusInfo(slot) {
      if (!slot.appointment) return null;
      return getPatientAdmissionStatus(slot.appointment.status);
    },

    enableSlot(slotId) {
      slotService
        .enableSlotById({
          slotId,
        })
        .then(() => {
          const index = this.selectedDateSlots.findIndex(
            (elem) => elem._id === slotId
          );

          const updatedSlot = {
            ...this.selectedDateSlots[index],
            available: true,
          };

          this.$set(this.selectedDateSlots, index, updatedSlot);
        })
        .then(() => this.$message.success("Se habilitó el horario con éxito"));
    },
  },
};
</script>

<template lang="pug">
  section.appointment
    BaseSubNav(
      navigationType="appointment"
      :showNavigation="true"
    )
      template(slot="title")
        | Calendario
        small(v-if="!isLoadingSlots && pagination.total") ({{ pagination.total }})

      template(slot="actions")
        el-select(
          v-model="selectedInstitutionId"
          placeholder="Institución"
          filterable
          clearable
          required
        )
          el-option(
            v-for="institution in institutions"
            :key="institution._id"
            :label="institution.name"
            :value="institution._id"
          )

        el-select(
          v-if="!isDoctor"
          v-model="selectedDoctorId"
          placeholder="Médico"
          filterable
          clearable
          :no-data-text="selectedInstitutionId ? 'No hay doctores para el hospital seleccionado' : 'Seleccione un hospital'"
          default-first-option
        )
          el-option(
            v-for="doctor in doctors"
            :key="doctor._id"
            :label="`${getFullName(doctor)}`"
            :value="doctor._id"
          )

        el-select(
          v-model="selectedMedicalSpecialtyId"
          placeholder="Especialidad"
          filterable
          clearable
          required
          :no-data-text="selectedDoctorId ? 'El doctor no tiene especialidades' : 'Seleccione un doctor'"
        )
          el-option(
            v-for="medicalSpecialty in medicalSpecialties"
            :key="medicalSpecialty._id"
            :label="medicalSpecialty.name"
            :value="medicalSpecialty._id"
          )

        el-select(
          v-model="selectedStatus"
          placeholder="Todas las citas"
          filterable
          clearable
          default-first-option
        )
          el-option(
            v-for="(item,key,index) in appointmentsStatus"
            :key="index"
            :label="item"
            :value="key"
          )

        .button.button--blue(
          v-if="isManualManagementEnabled && selectedDoctorId && selectedInstitutionId && selectedMedicalSpecialtyId && !isPatient"
          @click='startInstantAppointment'
          type="primary"
        ) + Sobreturno

        .button.button--blue(@click='getSlotsByDate')
          micon(name="refresh")
    .box
      section
        article.row
          nav.sidebar
            slots-calendar(
              :selectedDoctorId="selectedDoctorId"
              :selectedInstitutionId="selectedInstitutionId"
              :selectedMedicalSpecialtyId="selectedMedicalSpecialtyId"
              :isHL7AgendaEnabled="isHL7AgendaMode"
              )
          .box__content--stretch
            spinner(v-if="isLoadingSlots")
            h2.noAlerts(v-if="!selectedDate") Seleccionar una fecha
            div(v-if="selectedDate")
              div(v-if="selectedInstitutionId")
                div(v-if="selectedDoctorId")
                  h2.noAlerts(v-if='!selectedMedicalSpecialtyId && !isHL7AgendaMode') Seleccionar una especialidad médica
                div(v-else)
                  h2.noAlerts Seleccionar un doctor
              div(v-else)
                h2.noAlerts Seleccionar una institución
              h2.no-alerts(v-if="canLoadSlots && !isLoadingSlots && !selectedDateSlots.length") No se encontraron citas
            .box.box--with-subnav(v-if="selectedDate && !isLoadingSlots && selectedDateSlots.length")
              .box__content--stretch
                table
                  thead
                    tr
                      th Doctor
                      th Especialidad
                      th Tipo de cita
                      th Fecha
                      th Horario
                      th(v-if="isAdmissionTimestampEnabled") Fecha y hora de admisión
                      th
                      th
                  tbody
                    template(v-for="slot in filteredSlots")
                      tr(
                        :key="slot._id" @click="showModal(slot)" :class="[slot.available ? 'free' : 'busy', { 'instant': slot.appointment && slot.appointment.isInstant, 'has-observation': canEditSlotObservation(slot) }]")
                        td {{ getFullName(slot.doctor) }}
                        td {{ getMedicalSpecialtiesNames(slot) }}
                        td
                          .badge.attention(
                            v-for="type in attentionType(slot)",
                            :key="type"
                          ) {{ getAttentionTypeName(type) }}
                        td {{ slot.startsAt | formatDate}}
                        td {{ getStartAndEndHours(slot.startsAt, slot.endsAt)}}
                        td(v-if="isAdmissionTimestampEnabled") {{ slot.appointment && slot.appointment.admittedAt ? $options.filters.formatDateAndTime(slot.appointment.admittedAt) : '-' }}
                        td
                          .badge.active-room(v-if='hasActiveRoom(slot)' @click="goToAppointment(slot.appointment._id)") Paciente conectado
                          .badge.available-room(v-else-if='isVideoRoomEnabled(slot)') Sala habilitada
                          .badge(v-else-if="getStatusInfo(slot)" :class="[getStatusInfo(slot).buttonClass, { 'status-cancelled': slot.appointment && slot.appointment.status === 'cancelled' }]")
                            | {{getAppointmentLabel(slot)}}
                          .badge(v-else :class="slot.available ? 'free' : slot.appointment ? 'available-room' : 'busy'")
                            | {{getAppointmentLabel(slot)}}

                        td.actions
                          .actions-container
                            el-dropdown(trigger="click")
                              .button.button--action.el-dropdown-link(@click.stop)
                                micon(name="more_horiz")
                              el-dropdown-menu(slot="dropdown")
                                el-dropdown-item(v-if="!isAgendaReadOnlyMode && isAdmissionForDeragopyanEnabled && slot.appointment && slot.appointment.attentionType === 'in-person'")
                                  div(@click="startAppointment(slot.appointment._id)")
                                    micon(name="person")
                                    | Admisionar paciente
                                el-dropdown-item(v-if="!isAgendaReadOnlyMode && isManualManagementEnabled && slot.appointment")
                                  div(@click="cancelAppointment(slot.appointment._id)")
                                    micon(name="lock")
                                    | Cancelar cita
                                el-dropdown-item(v-if="!isAgendaReadOnlyMode && (slot.available || slot.appointment) && !(isSlotDisableHiddenForDoctor && isDoctor)")
                                  div(@click="disableSlot(slot._id)")
                                    micon(name="lock")
                                    | Deshabilitar horario
                                el-dropdown-item(v-if="!isAgendaReadOnlyMode && !slot.available && !slot.appointment")
                                  div(@click="enableSlot(slot._id)")
                                    micon(name="lock_open")
                                    | Habilitar horario
                      tr.observation-row(
                        v-if="canEditSlotObservation(slot)"
                        :key="slot._id + '-obs'"
                        :class="[slot.available ? 'free' : 'busy', { 'instant': slot.appointment && slot.appointment.isInstant }]"
                      )
                        td(:colspan="isAdmissionTimestampEnabled ? 8 : 7" @click.stop)
                          button.observation-add-btn(
                            v-if="shouldShowAddObservationButton(slot)"
                            type="button"
                            @click="startEditingObservation(slot)"
                          )
                            micon(name="add")
                            span Agregar observaciones
                          .observation-field(v-else)
                            span.observation-field__label Observaciones:
                            input.observation-field__input(
                              type="text"
                              v-model="slot.appointment.observation"
                              :ref="`obs-${slot.appointment._id}`"
                              placeholder="Escribir observación"
                            )
                            .observation-field__actions(v-if="isObservationDirty(slot)")
                              button.observation-btn.observation-btn--save(
                                type="button"
                                title="Guardar"
                                :disabled="isObservationSaving(slot)"
                                @click="saveObservation(slot)"
                              )
                                micon(name="check")
                              button.observation-btn.observation-btn--delete(
                                type="button"
                                title="Borrar"
                                :disabled="isObservationSaving(slot)"
                                @click="deleteObservation(slot)"
                              )
                                micon(name="close")

            pagination(
              :isLoading="isLoadingSlots"
              :limit="pagination.limit"
              :total="pagination.total"
              @pagination="setPagination"
            )

        modal-appointment(
          v-if='this.isModalVisible && this.isAppointmentBookingEnabled && !this.isAgendaReadOnlyMode'
          :show-dialog-modal='this.isModalVisible'
          :selected-slot='selectedSlot'
          :selectedMedicalSpecialty="getSelectedMedicalSpecialty"
          @close="closeModal"
        )
</template>

<style lang="scss" scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.box--with-subnav {
  box-shadow: none;
}

.box__content--stretch {
  position: relative;
}

h2 {
  margin-top: 20px;
  text-align: center;
}

td {
  .badge {
    margin-right: 5px;
    padding: 5px;
    border-radius: 3px;
    display: inline-block;
  }

  .busy {
    background-color: #fef0f0;
    border: 1px solid #fde2e2;
    color: #f56c6c;
  }

  .free {
    background-color: #f0f9eb;
    border: 1px solid #e1f3d8;
    color: #67c23a;
  }
}

.attention {
  background-color: white;
  border: 1px solid $primary;
  color: $primary;
}

.empty-row {
  td {
    border: none;
  }
}

.active-room {
  background-color: green;
  color: $white;
  cursor: pointer;
}

.available-room {
  background-color: $primary;
  color: $white;
}

tr.instant td {
  background-color: rgb(255 255 156 / 54%);

  &:first-child {
    border-left: 4px solid $corn;
  }
}

.status-cancelled {
  text-decoration: line-through;
}

.row {
  display: grid;
  grid-template-columns: 40% 60%;
}

tbody tr.has-observation > td {
  border-bottom: none;
}

tbody tr.observation-row {
  cursor: default;

  > td {
    padding: 0 16px 8px;
    border-top: none;
  }
}

.observation-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 4px;
  margin-left: 8px;
  background: transparent;
  border: none;
  color: #6b7785;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease, color 0.15s ease;

  micon {
    font-size: 14px;
  }

  &:hover {
    background-color: #f4f6fa;
    color: #556ba3;
  }
}

.observation-field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
}

.observation-field__label {
  color: #6b7785;
  font-size: 12px;
  font-weight: 600;
  user-select: none;
  flex-shrink: 0;
}

.observation-field__input {
  flex: 1;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 4px 8px;
  font-size: 13px;
  color: #4a5568;
  font-family: inherit;
  outline: none;
  border-radius: 4px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #b8c2cc;
    font-style: italic;
  }

  &:hover {
    border-color: #cbd5e0;
  }

  &:focus {
    border-color: #556ba3;
    box-shadow: 0 0 0 2px rgba(85, 107, 163, 0.15);
  }
}

.observation-field__actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.observation-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    opacity 0.15s ease;

  micon {
    font-size: 16px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--save {
    color: #2f855a;

    &:hover:not(:disabled) {
      background-color: #e6fffa;
      border-color: #2f855a;
    }
  }

  &--delete {
    color: #c53030;

    &:hover:not(:disabled) {
      background-color: #fff5f5;
      border-color: #c53030;
    }
  }
}
</style>
